import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate, getStatusColor } from '../lib/utils';
import { getApiErrorMessage } from '../lib/apiError';
import { analytics } from '../lib/analytics';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { toast } from 'sonner';
import { 
  FileText, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Plus,
  ArrowUpRight,
  TrendingUp,
  Palette,
  CreditCard,
  Send,
  X,
  CheckCircle2,
  Circle,
  MailCheck
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Dashboard = () => {
  const { user, api, refreshUser } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissedOnboarding, setDismissedOnboarding] = useState(false);
  const [trialOffer, setTrialOffer] = useState(null);
  const [trialDismissed, setTrialDismissed] = useState(
    sessionStorage.getItem('trial_offer_dismissed') === '1'
  );
  const [startingTrial, setStartingTrial] = useState(false);
  const prevVerifiedRef = useRef(user?.email_verified);

  useEffect(() => {
    fetchAnalytics();
    fetchTrialOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll /auth/me while unverified so a click on the emailed link (in another tab)
  // updates the dashboard right away.
  useEffect(() => {
    if (!user || user.email_verified) return;
    const id = setInterval(() => { refreshUser(); }, 10000);
    const onFocus = () => refreshUser();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [user, refreshUser]);

  // Celebrate the moment verification flips true — one-time per user.
  useEffect(() => {
    if (!user) return;
    const prev = prevVerifiedRef.current;
    if (!prev && user.email_verified) {
      const key = `cc_verified_celebrated_${user.user_id}`;
      if (!localStorage.getItem(key)) {
        toast.success('✨ Email verified — you can send invoices, campaigns and payments now.');
        localStorage.setItem(key, '1');
      }
    }
    prevVerifiedRef.current = user.email_verified;
  }, [user]);

  const handleSkipStep = async (stepKey) => {
    try {
      await api.post('/auth/onboarding/skip', { step: stepKey });
      await refreshUser();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not update the step.'));
    }
  };

  const handleResendVerification = async () => {
    try {
      await api.post('/auth/resend-verification');
      toast.success('Verification email sent — please check your inbox.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not send verification email.'));
    }
  };

  const fetchTrialOffer = async () => {
    try {
      const response = await api.get('/subscriptions/trial-offer');
      if (response.data.eligible) setTrialOffer(response.data);
    } catch (error) {
      // non-blocking
    }
  };

  const startTrial = async () => {
    setStartingTrial(true);
    analytics.beginCheckout(trialOffer.plan, true);
    try {
      const response = await api.post('/subscriptions/checkout', {
        plan: trialOffer.plan,
        origin_url: window.location.origin,
        trial: true,
      });
      window.location.href = response.data.url;
    } catch (error) {
      setStartingTrial(false);
    }
  };

  const dismissTrial = () => {
    sessionStorage.setItem('trial_offer_dismissed', '1');
    setTrialDismissed(true);
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/analytics');
      setAnalytics(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Onboarding steps
  const onboarding = user?.onboarding || {};
  const verifyDone = !!user?.email_verified;
  const showOnboarding = !dismissedOnboarding && (!verifyDone || !onboarding.is_complete);

  const onboardingSteps = [
    {
      key: 'has_email_verified',
      title: 'Verify your email',
      description: 'Confirm your address to unlock sending invoices, campaigns and payments.',
      done: verifyDone,
      link: '/welcome',
      icon: <MailCheck className="w-5 h-5" />
    },
    {
      key: 'has_branding',
      title: 'Add your branding',
      description: 'Your name, logo, and colors appear on every invoice.',
      done: onboarding.has_branding,
      link: '/settings',
      icon: <Palette className="w-5 h-5" />
    },
    {
      key: 'has_stripe_connected',
      title: 'Connect payments',
      description: 'Let clients pay your invoices by card. Money goes straight to you.',
      done: onboarding.has_stripe_connected,
      link: '/settings',
      icon: <CreditCard className="w-5 h-5" />
    },
    {
      key: 'has_sent_invoice',
      title: 'Send your first invoice',
      description: 'Takes about 60 seconds.',
      done: onboarding.has_sent_invoice,
      link: '/invoices/new',
      icon: <Send className="w-5 h-5" />,
      skippable: true
    }
  ];

  const completedSteps = onboardingSteps.filter(s => s.done).length;
  const totalSteps = onboardingSteps.length;

  const stats = analytics ? [
    {
      title: 'Total Revenue',
      value: formatCurrency(analytics.total_revenue),
      icon: <DollarSign className="w-5 h-5" />,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    },
    {
      title: 'Pending',
      value: formatCurrency(analytics.pending_amount),
      icon: <Clock className="w-5 h-5" />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Paid Invoices',
      value: analytics.paid_count,
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10'
    },
    {
      title: 'Partially Paid',
      value: analytics.partially_paid_count || 0,
      icon: <DollarSign className="w-5 h-5" />,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Overdue',
      value: analytics.overdue_count,
      icon: <AlertCircle className="w-5 h-5" />,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10'
    }
  ] : [];

  const planLimits = {
    free: { limit: 2, name: 'Free' },
    standard: { limit: 5, name: 'Standard' },
    pro: { limit: -1, name: 'Pro' }
  };

  const currentPlan = planLimits[user?.subscription_plan || 'free'];
  const invoicesUsed = user?.invoices_this_month || 0;
  const invoicesRemaining = currentPlan.limit === -1 ? 'Unlimited' : currentPlan.limit - invoicesUsed;

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-7xl mx-auto space-y-8" data-testid="dashboard-page">
        <EmailVerificationBanner />
        {/* Ambassador trial offer */}
        {trialOffer && !trialDismissed && (
          <Card className="bg-primary text-primary-foreground border-foreground/10" data-testid="trial-offer-card">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2 flex-1">
                  <h2 className="font-heading font-bold text-xl" data-testid="trial-offer-heading">
                    Your {trialOffer.months} month{trialOffer.months !== 1 ? 's' : ''} of {trialOffer.plan_name} are waiting
                  </h2>
                  <p className="text-sm opacity-90" data-testid="trial-offer-body">
                    Add a card to start your free {trialOffer.months} month{trialOffer.months !== 1 ? 's' : ''}. You won&apos;t be charged until {trialOffer.first_charge_date}, and you can cancel anytime before then.
                  </p>
                  <p className="text-xs opacity-80" data-testid="trial-offer-disclosure">
                    First charge: <strong>${trialOffer.price.toFixed(2)}/mo on {trialOffer.first_charge_date}</strong> · Cancel anytime in Settings
                  </p>
                  <Button onClick={startTrial} disabled={startingTrial} variant="secondary" className="gap-2 mt-1" data-testid="start-trial-btn">
                    <CreditCard className="w-4 h-4" />
                    {startingTrial ? 'Opening checkout...' : `Start free ${trialOffer.months} month${trialOffer.months !== 1 ? 's' : ''}`}
                  </Button>
                </div>
                <button onClick={dismissTrial} className="p-1 rounded hover:bg-primary-foreground/10 text-primary-foreground/80 shrink-0" aria-label="Dismiss" data-testid="trial-offer-dismiss">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Onboarding Checklist */}
        {showOnboarding && (
          <Card className="bg-accent text-accent-foreground border-2 border-foreground/80" data-testid="onboarding-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] opacity-80 block mb-1">
                      ★ &nbsp; New here
                    </span>
                    <CardTitle className="text-2xl md:text-3xl font-heading font-bold tracking-tight">Get set up in {totalSteps} steps</CardTitle>
                    <CardDescription className="text-accent-foreground/80 font-mono text-xs uppercase tracking-widest mt-2">
                      {completedSteps}/{totalSteps} completed
                    </CardDescription>
                  </div>
                  <img
                    src="https://res.cloudinary.com/nwvsq3gc/image/upload/e_trim/w_150,q_auto,f_auto/v1788391503/CC_clock_blue.png"
                    alt="Hand-illustrated clock character"
                    className="hidden sm:block w-14 h-14 shrink-0"
                    data-testid="onboarding-illustration"
                  />
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-accent-foreground/70 hover:text-accent-foreground hover:bg-accent-foreground/10"
                  onClick={() => setDismissedOnboarding(true)}
                  data-testid="dismiss-onboarding"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {onboardingSteps.map((step) => (
                <div 
                  key={step.key} 
                  className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                    step.done ? 'bg-accent-foreground/5' : 'bg-accent-foreground/10 hover:bg-accent-foreground/15'
                  }`}
                  data-testid={`onboarding-step-${step.key}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    step.done ? 'bg-primary text-primary-foreground' : 'bg-accent-foreground/15 text-accent-foreground'
                  }`}>
                    {step.done ? <CheckCircle2 className="w-5 h-5" /> : step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm ${step.done ? 'line-through opacity-60' : ''}`}>
                      {step.title}
                    </p>
                    <p className="text-xs opacity-75 truncate">{step.description}</p>
                  </div>
                  {!step.done && (
                    <div className="flex items-center gap-2 shrink-0">
                      {step.key === 'has_email_verified' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleResendVerification}
                          className="text-accent-foreground hover:bg-accent-foreground/10"
                          data-testid="onboarding-resend-verification-btn"
                        >
                          Resend
                        </Button>
                      )}
                      <Link to={step.link}>
                        <Button size="sm" className="bg-foreground text-background hover:bg-foreground/90">
                          Start
                        </Button>
                      </Link>
                      {step.skippable && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSkipStep(step.key)}
                          className="text-accent-foreground/70 hover:text-accent-foreground hover:bg-accent-foreground/10 whitespace-nowrap"
                          data-testid={`onboarding-skip-${step.key}-btn`}
                        >
                          I&apos;ll do this later
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Here&apos;s what&apos;s happening with your invoices
            </p>
          </div>
          <Link to="/invoices/new">
            <Button className="gap-2 shadow-lg shadow-primary/20" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4" />
              New Invoice
            </Button>
          </Link>
        </div>

        {/* Plan Status */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-medium">
                  {currentPlan.name} Plan
                  {currentPlan.limit !== -1 && (
                    <span className="text-muted-foreground ml-2 text-sm">
                      ({invoicesUsed}/{currentPlan.limit} invoices this month)
                    </span>
                  )}
                </p>
                <p className="text-sm text-muted-foreground">
                  {typeof invoicesRemaining === 'number' 
                    ? `${invoicesRemaining} invoices remaining this month`
                    : 'Unlimited invoices'}
                </p>
              </div>
            </div>
            {user?.subscription_plan !== 'pro' && (
              <Link to="/settings">
                <Button variant="outline" size="sm" data-testid="upgrade-btn">
                  Upgrade Plan
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
          {stats.map((stat, index) => (
            <Card key={index} className="hover:shadow-md transition-shadow duration-200" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center ${stat.bgColor} ${stat.color}`}>
                    {stat.icon}
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-heading font-bold">{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.title}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Revenue Overview</CardTitle>
              <CardDescription>Monthly revenue from paid invoices</CardDescription>
            </CardHeader>
            <CardContent>
              {analytics?.monthly_revenue?.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.monthly_revenue}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="month" 
                        className="text-xs fill-muted-foreground"
                        tickFormatter={(value) => {
                          const [year, month] = value.split('-');
                          return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short' });
                        }}
                      />
                      <YAxis 
                        className="text-xs fill-muted-foreground"
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip 
                        formatter={(value) => [formatCurrency(value), 'Revenue']}
                        labelFormatter={(value) => {
                          const [year, month] = value.split('-');
                          return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                        }}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="hsl(var(--primary))" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <p>No revenue data yet. Create and get paid for your first invoice!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Invoices */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Recent Invoices</CardTitle>
                <CardDescription>Your latest activity</CardDescription>
              </div>
              <Link to="/invoices">
                <Button variant="ghost" size="sm" className="gap-1">
                  View all <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {analytics?.recent_invoices?.length > 0 ? (
                <ul className="space-y-4">
                  {analytics.recent_invoices.map((invoice) => (
                    <li key={invoice.invoice_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{invoice.client_name}</p>
                          <p className="text-xs text-muted-foreground">{invoice.invoice_number}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm font-medium">{formatCurrency(invoice.total)}</p>
                        <Badge variant="secondary" className={`text-xs ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </Badge>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No invoices yet</p>
                  <Link to="/invoices/new">
                    <Button variant="link" className="mt-2">Create your first invoice</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
